import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { logOut } from '@/state/reducers/auth.slice';
import AuthService from '@/lib/auth';
import biometricAuthService from '@/services/biometricAuthService';
import { isStaffRole } from '@/utils/mfaEmail';

// Inactivity timeouts:
// 10 minutes for Staff Roles (Doctor, Nurse, Admin, etc.)
// 15 minutes for Patients (kept as-is)
const STAFF_INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const STAFF_WARNING_TIMEOUT_MS = 9 * 60 * 1000;     // 9 minutes (1 min warning)

const PATIENT_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const PATIENT_WARNING_TIMEOUT_MS = 14 * 60 * 1000;    // 14 minutes (1 min warning)

const THROTTLE_INTERVAL_MS = 1000; // Throttle activity listener to 1s

const PUBLIC_PATHS = [
  '/login',
  '/auth/register',
  '/auth/forgot-password',
  '/verify-email',
  '/auth/callback',
];

export function useSessionTimeout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAppSelector((state) => state.auth);

  const lastActivityRef = useRef<number>(Date.now());
  const warningToastIdRef = useRef<string | number | null>(null);
  const isLoggingOutRef = useRef<boolean>(false);

  const isPublicPage = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p)) ||
    location.pathname.startsWith('/q/') ||
    location.pathname.startsWith('/c/') ||
    location.pathname.startsWith('/public/');

  const isAuthenticated = !!(auth?.token && auth?.user?.id && !isPublicPage);
  const isStaff = isStaffRole(auth?.user?.role);
  const inactivityTimeoutMs = isStaff ? STAFF_INACTIVITY_TIMEOUT_MS : PATIENT_INACTIVITY_TIMEOUT_MS;
  const warningTimeoutMs = isStaff ? STAFF_WARNING_TIMEOUT_MS : PATIENT_WARNING_TIMEOUT_MS;
  const timeoutMinutes = isStaff ? 10 : 15;

  const handleLogout = useCallback(async (reason: 'inactivity' | 'manual' = 'inactivity') => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    try {
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current);
        warningToastIdRef.current = null;
      }

      // Clear tokens, credentials, and MFA session flag
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('jwt_token_expiry');
      sessionStorage.removeItem('mfa_verified');
      biometricAuthService.clearAuthData();

      // Sign out from AWS Amplify and Redux
      await AuthService.signOut();
      dispatch(logOut());

      if (reason === 'inactivity') {
        toast.error('Session timed out', {
          description: `You were logged out due to ${timeoutMinutes} minutes of inactivity for HIPAA security.`,
          duration: 8000,
        });
      }
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Session logout error:', err);
      dispatch(logOut());
      navigate('/login', { replace: true });
    } finally {
      isLoggingOutRef.current = false;
    }
  }, [dispatch, navigate, timeoutMinutes]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    lastActivityRef.current = Date.now();

    let lastRecorded = 0;
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastRecorded > THROTTLE_INTERVAL_MS) {
        lastRecorded = now;
        resetActivity();
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, handleUserActivity, { passive: true }));

    // Check timer every 10 seconds
    const intervalId = setInterval(() => {
      if (!isAuthenticated || isLoggingOutRef.current) return;

      const idleDuration = Date.now() - lastActivityRef.current;

      // Show warning at 1 minute before expiration
      if (idleDuration >= warningTimeoutMs && idleDuration < inactivityTimeoutMs) {
        if (!warningToastIdRef.current) {
          warningToastIdRef.current = toast.warning('Session Expiring Soon', {
            description: `Due to inactivity, your session will end in 1 minute. (${isStaff ? 'Staff limit: 10 mins' : 'Session limit: 15 mins'})`,
            action: {
              label: 'Stay Signed In',
              onClick: () => resetActivity(),
            },
            duration: 55000,
          });
        }
      }

      // Terminate session at timeout threshold
      if (idleDuration >= inactivityTimeoutMs) {
        handleLogout('inactivity');
      }
    }, 10000);

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handleUserActivity));
      clearInterval(intervalId);
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current);
      }
    };
  }, [isAuthenticated, isStaff, inactivityTimeoutMs, warningTimeoutMs, resetActivity, handleLogout]);

  return { resetActivity, handleLogout };
}
